import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Plus, Pencil, Trash2, Key, Loader2, UserCog, Shield } from "lucide-react";
import type { AdminSession } from "@/components/layouts/admin-layout";

interface AdminRole {
  id: string;
  name: string;
}

interface AdminUser {
  id: string;
  username: string;
  displayName: string;
  roleId: string | null;
  roleName: string | null;
  isSuperAdmin: boolean;
  createdAt: string;
}

export default function AdminUsersPage() {
  const { toast } = useToast();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<AdminUser | null>(null);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [userForPassword, setUserForPassword] = useState<AdminUser | null>(null);

  const [formData, setFormData] = useState({
    username: "",
    displayName: "",
    password: "",
  });

  const [newPassword, setNewPassword] = useState("");

  const { data: users, isLoading } = useQuery<AdminUser[]>({
    queryKey: ["/api/admin/users"],
  });

  const { data: roles = [] } = useQuery<AdminRole[]>({
    queryKey: ["/api/admin/roles"],
  });

  const { data: currentAdmin } = useQuery<AdminSession | null>({
    queryKey: ["/api/admin-auth/me"],
  });

  const createMutation = useMutation({
    mutationFn: async (data: { username: string; displayName: string; password: string }) => {
      return apiRequest("POST", "/api/admin/users", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/users"] });
      toast({ title: "Användare skapad!" });
      closeDialog();
    },
    onError: (error: Error) => {
      toast({ title: "Fel", description: error.message, variant: "destructive" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: { username?: string; displayName?: string } }) => {
      return apiRequest("PATCH", `/api/admin/users/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/users"] });
      toast({ title: "Användare uppdaterad!" });
      closeDialog();
    },
    onError: (error: Error) => {
      toast({ title: "Fel", description: error.message, variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      return apiRequest("DELETE", `/api/admin/users/${id}`, {});
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/users"] });
      toast({ title: "Användare borttagen!" });
      setDeleteConfirmOpen(false);
      setUserToDelete(null);
    },
    onError: (error: Error) => {
      toast({ title: "Fel", description: error.message, variant: "destructive" });
    },
  });

  const changePasswordMutation = useMutation({
    mutationFn: async ({ id, password }: { id: string; password: string }) => {
      return apiRequest("POST", `/api/admin/users/${id}/change-password`, { password });
    },
    onSuccess: () => {
      toast({ title: "Lösenord ändrat!" });
      setPasswordDialogOpen(false);
      setUserForPassword(null);
      setNewPassword("");
    },
    onError: (error: Error) => {
      toast({ title: "Fel", description: error.message, variant: "destructive" });
    },
  });

  const assignRoleMutation = useMutation({
    mutationFn: async ({ adminId, roleId }: { adminId: string; roleId: string | null }) => {
      return apiRequest("POST", "/api/admin/roles/assign", { adminId, roleId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/users"] });
      toast({ title: "Roll tilldelad!" });
    },
    onError: (error: Error) => {
      toast({ title: "Fel", description: error.message, variant: "destructive" });
    },
  });

  const openCreateDialog = () => {
    setEditingUser(null);
    setFormData({ username: "", displayName: "", password: "" });
    setDialogOpen(true);
  };

  const openEditDialog = (user: AdminUser) => {
    setEditingUser(user);
    setFormData({ username: user.username, displayName: user.displayName, password: "" });
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingUser(null);
    setFormData({ username: "", displayName: "", password: "" });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingUser) {
      updateMutation.mutate({
        id: editingUser.id,
        data: { username: formData.username, displayName: formData.displayName },
      });
    } else {
      if (!formData.password) {
        toast({ title: "Fel", description: "Lösenord krävs för ny användare", variant: "destructive" });
        return;
      }
      createMutation.mutate(formData);
    }
  };

  const handleDelete = (user: AdminUser) => {
    setUserToDelete(user);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = () => {
    if (userToDelete) {
      deleteMutation.mutate(userToDelete.id);
    }
  };

  const handleChangePassword = (user: AdminUser) => {
    setUserForPassword(user);
    setNewPassword("");
    setPasswordDialogOpen(true);
  };

  const submitPasswordChange = (e: React.FormEvent) => {
    e.preventDefault();
    if (userForPassword && newPassword) {
      changePasswordMutation.mutate({ id: userForPassword.id, password: newPassword });
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;
  const isSuperAdmin = currentAdmin?.isSuperAdmin ?? false;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Användare</h1>
          <p className="text-muted-foreground">Hantera administratörskonton</p>
        </div>
        {isSuperAdmin && (
          <Button onClick={openCreateDialog} data-testid="button-create-user">
            <Plus className="h-4 w-4 mr-2" />
            Lägg till användare
          </Button>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserCog className="h-5 w-5" />
            Administratörer
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center p-8">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Användarnamn</TableHead>
                    <TableHead>Visningsnamn</TableHead>
                    <TableHead>Roll</TableHead>
                    <TableHead>Skapad</TableHead>
                    {isSuperAdmin && <TableHead className="text-right">Åtgärder</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users?.map((user) => (
                    <TableRow key={user.id} data-testid={`row-user-${user.id}`}>
                      <TableCell className="font-medium">{user.username}</TableCell>
                      <TableCell>{user.displayName}</TableCell>
                      <TableCell>
                        {user.isSuperAdmin ? (
                          <Badge variant="default" className="gap-1" data-testid={`badge-superadmin-${user.id}`}>
                            <Shield className="h-3 w-3" />
                            Super-admin
                          </Badge>
                        ) : isSuperAdmin ? (
                          <Select
                            value={user.roleId ?? "none"}
                            onValueChange={(val) =>
                              assignRoleMutation.mutate({
                                adminId: user.id,
                                roleId: val === "none" ? null : val,
                              })
                            }
                          >
                            <SelectTrigger
                              className="h-7 text-xs w-36"
                              data-testid={`select-role-${user.id}`}
                            >
                              <SelectValue placeholder="Ingen roll" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">Ingen roll</SelectItem>
                              {roles.map(role => (
                                <SelectItem key={role.id} value={role.id} data-testid={`option-role-${role.id}`}>
                                  {role.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <span className="text-sm text-muted-foreground" data-testid={`text-role-${user.id}`}>
                            {user.roleName ?? "Ingen roll"}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        {new Date(user.createdAt).toLocaleDateString("sv-SE")}
                      </TableCell>
                      {isSuperAdmin && (
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1 flex-wrap">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleChangePassword(user)}
                              title="Ändra lösenord"
                              data-testid={`button-change-password-${user.id}`}
                            >
                              <Key className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openEditDialog(user)}
                              title="Redigera"
                              data-testid={`button-edit-user-${user.id}`}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDelete(user)}
                              disabled={currentAdmin?.id === user.id || user.isSuperAdmin}
                              title={
                                user.isSuperAdmin
                                  ? "Super-admin kan inte tas bort"
                                  : currentAdmin?.id === user.id
                                  ? "Du kan inte ta bort dig själv"
                                  : "Ta bort"
                              }
                              data-testid={`button-delete-user-${user.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                  {users?.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={isSuperAdmin ? 5 : 4} className="text-center text-muted-foreground py-8">
                        Inga användare hittades
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingUser ? "Redigera användare" : "Skapa ny användare"}
            </DialogTitle>
            <DialogDescription>
              {editingUser
                ? "Uppdatera användarinformation nedan."
                : "Fyll i uppgifterna för den nya administratören."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="username">Användarnamn</Label>
                <Input
                  id="username"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  placeholder="admin"
                  required
                  data-testid="input-username"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="displayName">Visningsnamn</Label>
                <Input
                  id="displayName"
                  value={formData.displayName}
                  onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                  placeholder="Administrator"
                  required
                  data-testid="input-displayname"
                />
              </div>
              {!editingUser && (
                <div className="space-y-2">
                  <Label htmlFor="password">Lösenord</Label>
                  <Input
                    id="password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="••••••••"
                    required
                    data-testid="input-password"
                  />
                </div>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={closeDialog}>
                Avbryt
              </Button>
              <Button type="submit" disabled={isPending} data-testid="button-submit-user">
                {isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {editingUser ? "Spara" : "Skapa"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ändra lösenord</DialogTitle>
            <DialogDescription>
              Ange ett nytt lösenord för {userForPassword?.displayName}.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitPasswordChange}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="newPassword">Nytt lösenord</Label>
                <Input
                  id="newPassword"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  data-testid="input-new-password"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setPasswordDialogOpen(false)}>
                Avbryt
              </Button>
              <Button
                type="submit"
                disabled={changePasswordMutation.isPending}
                data-testid="button-submit-password"
              >
                {changePasswordMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Ändra lösenord
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Ta bort användare?</AlertDialogTitle>
            <AlertDialogDescription>
              Är du säker på att du vill ta bort användaren "{userToDelete?.displayName}"?
              Denna åtgärd kan inte ångras.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="button-confirm-delete"
            >
              {deleteMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Ta bort
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
